/**
 * Key poses without keyframe assets: a one-shot is a short list of pose FUNCTIONS at phases
 * 0..1, and the frame between two of them is a per-bone slerp. The callers feed this a clock
 * already stepped "on twos", so the in-betweens come out as held drawings, not as a glide.
 */

import { Quaternion, Vector3 } from 'three';
import { type BoneOf, resetPose, type Rig } from './poseBase';
import { BONE_COUNT } from './rig';

export type PoseFn = (bone: BoneOf) => void;
export type Keys = ReadonlyArray<readonly [at: number, pose: PoseFn]>;

/* Scratch: the per-frame path allocates nothing. */
const SNAP = Array.from({ length: BONE_COUNT }, () => ({ p: new Vector3(), q: new Quaternion() }));
const _q = new Quaternion();
const _p = new Vector3();

const smooth = (k: number): number => k * k * (3 - 2 * k);

/** Leave the rig `k` of the way from pose `from` to pose `to` (both written from the rest pose). */
export function blendPoses(rig: Rig, from: PoseFn, to: PoseFn, k: number): void {
  resetPose(rig.bones);
  if (k >= 1) {
    to(rig.bone);
    return;
  }
  from(rig.bone);
  if (k <= 0) return;
  rig.bones.forEach((b, i) => {
    const snap = SNAP[i];
    if (!snap) return;
    snap.p.copy(b.position);
    snap.q.copy(b.quaternion);
  });
  resetPose(rig.bones);
  to(rig.bone);
  rig.bones.forEach((b, i) => {
    const snap = SNAP[i];
    if (!snap) return;
    _p.copy(b.position);
    _q.copy(b.quaternion);
    b.position.lerpVectors(snap.p, _p, k);
    // Writing the quaternion keeps the Euler in step (three.js syncs the two both ways).
    b.quaternion.slerpQuaternions(snap.q, _q, k);
  });
}

/** Pose the rig at `phase` (0..1) of a keyed one-shot. Before the first key and after the last, that key holds. */
export function playKeys(rig: Rig, keys: Keys, phase: number): void {
  const first = keys[0];
  const last = keys[keys.length - 1];
  if (!first || !last) return;
  if (phase <= first[0]) return blendPoses(rig, first[1], first[1], 1);
  if (phase >= last[0]) return blendPoses(rig, last[1], last[1], 1);
  for (let i = 1; i < keys.length; i++) {
    const a = keys[i - 1];
    const b = keys[i];
    if (!a || !b || phase > b[0]) continue;
    return blendPoses(rig, a[1], b[1], smooth((phase - a[0]) / Math.max(1e-6, b[0] - a[0])));
  }
}
